import { select } from 'd3-selection';

import { isMotionFull, subscribeMotion } from './motion';
import {
  createGlobeProjection,
  disableAxisDrag,
  enableAxisDrag,
  fitGlobeProjection,
  type GlobeGroup,
  type GlobeProjection,
  type GlobeSvg,
  renderOrthographicGlobe,
  setAxisRotation,
} from './orthographic-globe';

const TURN_MS = 3000;
const TURN_DEGREES = 360;
const CRUISE_TURN_MS = 30_000;
const CRUISE_DEG_PER_MS = TURN_DEGREES / CRUISE_TURN_MS;
/** Longitude of Geneva, the Summit city — rest facing of the Globe. */
const GENEVA_LNG = 6.1432;

/**
 * Cubic Hermite on [0, 1]: rest at t=0, a full turn at t=1, end speed equal to
 * cruise so the intro hands off without stopping.
 */
const introDegrees = (t: number): number => {
  const t2 = t * t;
  const t3 = t2 * t;
  const endTangent = CRUISE_DEG_PER_MS * TURN_MS;
  return TURN_DEGREES * (-2 * t3 + 3 * t2) + endTangent * (t3 - t2);
};

class GlobeController {
  private readonly container: HTMLElement;
  private svg!: GlobeSvg;
  private globeGroup!: GlobeGroup;
  private readonly projection: GlobeProjection = createGlobeProjection();

  private width = 0;
  private height = 0;
  private lng = GENEVA_LNG;
  private animationFrame = 0;
  private ticking = false;
  private visible = false;
  private introPlayed = false;
  private phase: 'intro' | 'cruise' = 'intro';
  private introElapsed = 0;
  private introOrigin: number | null = null;
  private cruiseOrigin: number | null = null;
  private dragEnabled = false;
  private intersectionObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private unsubscribeMotion: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.mountSvg();
  }

  init(): void {
    this.applyLng();
    this.observeResize();
    this.observeVisibility();
    this.syncDrag();
    this.unsubscribeMotion = subscribeMotion(() => {
      this.onMotionTier();
    });
    document.addEventListener('visibilitychange', this.onPageVisibility);
  }

  destroy(): void {
    this.stopClock();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.unsubscribeMotion?.();
    document.removeEventListener('visibilitychange', this.onPageVisibility);
    this.disableDrag();
  }

  private mountSvg(): void {
    const svg = select(this.container)
      .append('svg')
      .attr('class', 'size-full touch-none')
      .attr('role', 'presentation');

    svg.append('g').attr('data-globe', '');

    this.svg = svg;
    this.globeGroup = svg.select<SVGGElement>('[data-globe]');
  }

  private applyLng(): void {
    setAxisRotation(this.projection, this.lng);
  }

  private syncLngFromProjection(): void {
    this.lng = -this.projection.rotate()[0];
  }

  private observeVisibility(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.visible = entry.isIntersecting;
          if (!isMotionFull() && this.visible) {
            this.introPlayed = true;
          }
          this.syncPlayback();
        }
      },
      { threshold: 0.35 },
    );

    this.intersectionObserver.observe(this.container);
  }

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });

    this.resizeObserver.observe(this.container);
    this.resize();
  }

  private resize(): void {
    const rect = this.container.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;

    fitGlobeProjection(this.projection, this.width, this.height);
    this.svg.attr('viewBox', `0 0 ${this.width} ${this.height}`);
    this.render();
  }

  private render(): void {
    renderOrthographicGlobe(
      this.globeGroup,
      this.projection,
      this.width,
      this.height,
    );
  }

  private onMotionTier(): void {
    if (!isMotionFull() && this.visible) {
      this.introPlayed = true;
    }
    this.stopClock();
    this.syncPlayback();
    this.syncDrag();
  }

  private onPageVisibility = (): void => {
    if (document.visibilityState !== 'visible') {
      this.stopClock();
      return;
    }

    this.syncPlayback();
  };

  private shouldSpin(): boolean {
    return (
      this.visible && isMotionFull() && document.visibilityState === 'visible'
    );
  }

  private syncPlayback(): void {
    if (this.shouldSpin()) {
      this.startClock();
      return;
    }

    this.stopClock();
  }

  private startClock(): void {
    if (this.ticking) {
      return;
    }

    this.ticking = true;
    this.phase = this.introPlayed ? 'cruise' : 'intro';
    this.animationFrame = requestAnimationFrame(this.onFrame);
  }

  private stopClock(): void {
    this.ticking = false;
    cancelAnimationFrame(this.animationFrame);
    this.introOrigin = null;
    this.cruiseOrigin = null;
  }

  private onFrame = (now: number): void => {
    if (!this.ticking) {
      return;
    }

    if (this.phase === 'intro') {
      if (this.introOrigin === null) {
        this.introOrigin = now - this.introElapsed;
      }

      this.introElapsed = now - this.introOrigin;
      const t = Math.min(this.introElapsed / TURN_MS, 1);
      this.lng = GENEVA_LNG + introDegrees(t);
      this.applyLng();
      this.render();

      if (t < 1) {
        this.animationFrame = requestAnimationFrame(this.onFrame);
        return;
      }

      this.introPlayed = true;
      this.phase = 'cruise';
      this.cruiseOrigin = now;
    }

    if (this.cruiseOrigin === null) {
      this.cruiseOrigin = now;
    }

    const dt = now - this.cruiseOrigin;
    this.cruiseOrigin = now;
    this.lng += CRUISE_DEG_PER_MS * dt;
    this.applyLng();
    this.render();
    this.animationFrame = requestAnimationFrame(this.onFrame);
  };

  private syncDrag(): void {
    if (isMotionFull()) {
      this.disableDrag();
      return;
    }

    this.enableDrag();
  }

  private enableDrag(): void {
    if (this.dragEnabled) {
      return;
    }

    this.dragEnabled = true;
    enableAxisDrag(this.svg, this.projection, () => {
      this.syncLngFromProjection();
      this.render();
    });
  }

  private disableDrag(): void {
    if (!this.dragEnabled) {
      return;
    }

    this.dragEnabled = false;
    this.syncLngFromProjection();
    disableAxisDrag(this.svg);
  }
}

const initialized = new WeakSet<HTMLElement>();

export const initGlobe = (element: HTMLElement): (() => void) => {
  if (initialized.has(element)) {
    return () => {};
  }

  initialized.add(element);
  const controller = new GlobeController(element);
  controller.init();

  return () => {
    controller.destroy();
    initialized.delete(element);
  };
};

for (const element of document.querySelectorAll<HTMLElement>('[data-globe-root]')) {
  initGlobe(element);
}
